export interface TarotCard {
  number: number
  name: string
  emoji: string
  image: string // Rider-Waite card image
  keywords: string[]
  uprightMeaning: string
  reversedMeaning: string
}

// Rider-Waite tarot card images (public domain)
const TAROT_IMAGE_BASE = 'https://upload.wikimedia.org/wikipedia/commons/thumb'

export const MAJOR_ARCANA: TarotCard[] = [
  {
    number: 0,
    name: 'The Fool',
    emoji: '🎭',
    image: `${TAROT_IMAGE_BASE}/9/90/RWS_Tarot_00_Fool.jpg/150px-RWS_Tarot_00_Fool.jpg`,
    keywords: ['new beginnings', 'innocence', 'spontaneity', 'free spirit'],
    uprightMeaning: 'A fresh start awaits. Embrace new adventures with an open heart and trust in the journey ahead.',
    reversedMeaning: 'Time to look before you leap. Reckless decisions may lead you astray - pause and reflect.',
  },
  {
    number: 1,
    name: 'The Magician',
    emoji: '⚡',
    image: `${TAROT_IMAGE_BASE}/d/de/RWS_Tarot_01_Magician.jpg/150px-RWS_Tarot_01_Magician.jpg`,
    keywords: ['manifestation', 'resourcefulness', 'power', 'inspired action'],
    uprightMeaning: 'You have all the tools you need. Channel your willpower and watch your intentions become reality.',
    reversedMeaning: 'Untapped potential lies dormant. Beware of manipulation or wasted talents.',
  },
  {
    number: 2,
    name: 'The High Priestess',
    emoji: '🌙',
    image: `${TAROT_IMAGE_BASE}/8/88/RWS_Tarot_02_High_Priestess.jpg/150px-RWS_Tarot_02_High_Priestess.jpg`,
    keywords: ['intuition', 'sacred knowledge', 'mystery', 'the subconscious'],
    uprightMeaning: 'Trust your inner voice. Hidden knowledge will be revealed when you listen to your intuition.',
    reversedMeaning: 'Secrets and withdrawal cloud your path. Reconnect with your inner wisdom.',
  },
  {
    number: 3,
    name: 'The Empress',
    emoji: '👑',
    image: `${TAROT_IMAGE_BASE}/d/d2/RWS_Tarot_03_Empress.jpg/150px-RWS_Tarot_03_Empress.jpg`,
    keywords: ['femininity', 'beauty', 'nature', 'abundance'],
    uprightMeaning: 'Abundance flows to you. Nurture your creative projects and watch them flourish.',
    reversedMeaning: 'Creative blocks or dependency issues arise. Reconnect with nature and self-care.',
  },
  {
    number: 4,
    name: 'The Emperor',
    emoji: '🏰',
    image: `${TAROT_IMAGE_BASE}/c/c3/RWS_Tarot_04_Emperor.jpg/150px-RWS_Tarot_04_Emperor.jpg`,
    keywords: ['authority', 'structure', 'control', 'fatherhood'],
    uprightMeaning: 'Take charge of your domain. Structure and discipline will build lasting success.',
    reversedMeaning: 'Rigidity or domination causes problems. Flexibility brings better results.',
  },
  {
    number: 5,
    name: 'The Hierophant',
    emoji: '📿',
    image: `${TAROT_IMAGE_BASE}/8/8d/RWS_Tarot_05_Hierophant.jpg/150px-RWS_Tarot_05_Hierophant.jpg`,
    keywords: ['tradition', 'conformity', 'morality', 'ethics'],
    uprightMeaning: 'Seek guidance from mentors or traditions. Established wisdom lights your path.',
    reversedMeaning: 'Question outdated beliefs. Your own truth may differ from convention.',
  },
  {
    number: 6,
    name: 'The Lovers',
    emoji: '💕',
    image: `${TAROT_IMAGE_BASE}/3/3a/RWS_Tarot_06_Lovers.jpg/150px-RWS_Tarot_06_Lovers.jpg`,
    keywords: ['love', 'harmony', 'relationships', 'choices'],
    uprightMeaning: 'Love and meaningful connections deepen. Important choices align with your values.',
    reversedMeaning: 'Disharmony in relationships or values. Reflect on what truly matters to you.',
  },
  {
    number: 7,
    name: 'The Chariot',
    emoji: '🏆',
    image: `${TAROT_IMAGE_BASE}/9/9b/RWS_Tarot_07_Chariot.jpg/150px-RWS_Tarot_07_Chariot.jpg`,
    keywords: ['control', 'willpower', 'success', 'determination'],
    uprightMeaning: 'Victory through determination. Harness opposing forces and charge toward your goals.',
    reversedMeaning: 'Lack of direction or control. Scattered energy prevents progress.',
  },
  {
    number: 8,
    name: 'Strength',
    emoji: '🦁',
    image: `${TAROT_IMAGE_BASE}/f/f5/RWS_Tarot_08_Strength.jpg/150px-RWS_Tarot_08_Strength.jpg`,
    keywords: ['courage', 'persuasion', 'influence', 'compassion'],
    uprightMeaning: 'Inner strength conquers all. Gentle persistence achieves what force cannot.',
    reversedMeaning: 'Self-doubt undermines your power. Reconnect with your inner lion.',
  },
  {
    number: 9,
    name: 'The Hermit',
    emoji: '🏔️',
    image: `${TAROT_IMAGE_BASE}/4/4d/RWS_Tarot_09_Hermit.jpg/150px-RWS_Tarot_09_Hermit.jpg`,
    keywords: ['soul-searching', 'introspection', 'solitude', 'inner guidance'],
    uprightMeaning: 'Seek solitude for answers. The light you need comes from within.',
    reversedMeaning: 'Isolation becomes loneliness. Balance reflection with connection.',
  },
  {
    number: 10,
    name: 'Wheel of Fortune',
    emoji: '🎡',
    image: `${TAROT_IMAGE_BASE}/3/3c/RWS_Tarot_10_Wheel_of_Fortune.jpg/150px-RWS_Tarot_10_Wheel_of_Fortune.jpg`,
    keywords: ['change', 'cycles', 'fate', 'turning points'],
    uprightMeaning: 'Fate spins in your favor. Embrace change as the wheel turns toward opportunity.',
    reversedMeaning: 'Resistance to change causes struggle. Adapt and flow with life\'s cycles.',
  },
  {
    number: 11,
    name: 'Justice',
    emoji: '⚖️',
    image: `${TAROT_IMAGE_BASE}/e/e0/RWS_Tarot_11_Justice.jpg/150px-RWS_Tarot_11_Justice.jpg`,
    keywords: ['fairness', 'truth', 'cause and effect', 'law'],
    uprightMeaning: 'Truth and fairness prevail. Your honest actions bring balanced outcomes.',
    reversedMeaning: 'Injustice or dishonesty creates imbalance. Seek accountability.',
  },
  {
    number: 12,
    name: 'The Hanged Man',
    emoji: '🙃',
    image: `${TAROT_IMAGE_BASE}/2/2b/RWS_Tarot_12_Hanged_Man.jpg/150px-RWS_Tarot_12_Hanged_Man.jpg`,
    keywords: ['pause', 'surrender', 'letting go', 'new perspectives'],
    uprightMeaning: 'Surrender to gain clarity. A pause brings the perspective shift you need.',
    reversedMeaning: 'Stalling or martyrdom holds you back. It\'s time to move forward.',
  },
  {
    number: 13,
    name: 'Death',
    emoji: '🦋',
    image: `${TAROT_IMAGE_BASE}/d/d7/RWS_Tarot_13_Death.jpg/150px-RWS_Tarot_13_Death.jpg`,
    keywords: ['endings', 'change', 'transformation', 'transition'],
    uprightMeaning: 'Transformation is here. Old chapters close so new ones can begin.',
    reversedMeaning: 'Resistance to necessary endings. What must you release to move forward?',
  },
  {
    number: 14,
    name: 'Temperance',
    emoji: '🌈',
    image: `${TAROT_IMAGE_BASE}/f/f8/RWS_Tarot_14_Temperance.jpg/150px-RWS_Tarot_14_Temperance.jpg`,
    keywords: ['balance', 'moderation', 'patience', 'purpose'],
    uprightMeaning: 'Harmony through balance. Patience and moderation create lasting peace.',
    reversedMeaning: 'Excess or imbalance disrupts flow. Seek the middle path.',
  },
  {
    number: 15,
    name: 'The Devil',
    emoji: '⛓️',
    image: `${TAROT_IMAGE_BASE}/5/55/RWS_Tarot_15_Devil.jpg/150px-RWS_Tarot_15_Devil.jpg`,
    keywords: ['shadow self', 'attachment', 'addiction', 'restriction'],
    uprightMeaning: 'Face your shadows. Awareness of unhealthy patterns is the first step to freedom.',
    reversedMeaning: 'Breaking free from bondage. Liberation comes through confronting fears.',
  },
  {
    number: 16,
    name: 'The Tower',
    emoji: '🗼',
    image: `${TAROT_IMAGE_BASE}/5/53/RWS_Tarot_16_Tower.jpg/150px-RWS_Tarot_16_Tower.jpg`,
    keywords: ['sudden change', 'upheaval', 'chaos', 'revelation'],
    uprightMeaning: 'Sudden change clears the path. What falls was built on shaky ground.',
    reversedMeaning: 'Delayed disaster or fear of change. Controlled demolition beats catastrophe.',
  },
  {
    number: 17,
    name: 'The Star',
    emoji: '⭐',
    image: `${TAROT_IMAGE_BASE}/d/db/RWS_Tarot_17_Star.jpg/150px-RWS_Tarot_17_Star.jpg`,
    keywords: ['hope', 'faith', 'purpose', 'renewal'],
    uprightMeaning: 'Hope illuminates darkness. Trust in the universe\'s plan for your renewal.',
    reversedMeaning: 'Lost faith or disconnection. Seek the spark that reignites your hope.',
  },
  {
    number: 18,
    name: 'The Moon',
    emoji: '🌕',
    image: `${TAROT_IMAGE_BASE}/7/7f/RWS_Tarot_18_Moon.jpg/150px-RWS_Tarot_18_Moon.jpg`,
    keywords: ['illusion', 'fear', 'anxiety', 'subconscious'],
    uprightMeaning: 'Navigate through uncertainty. Trust your intuition when the path is unclear.',
    reversedMeaning: 'Confusion clears as truth emerges. Face your fears to find clarity.',
  },
  {
    number: 19,
    name: 'The Sun',
    emoji: '☀️',
    image: `${TAROT_IMAGE_BASE}/1/17/RWS_Tarot_19_Sun.jpg/150px-RWS_Tarot_19_Sun.jpg`,
    keywords: ['positivity', 'fun', 'warmth', 'success'],
    uprightMeaning: 'Radiant success and joy await. Bask in the warmth of good fortune.',
    reversedMeaning: 'Dimmed optimism or temporary setbacks. The sun will shine again.',
  },
  {
    number: 20,
    name: 'Judgement',
    emoji: '📯',
    image: `${TAROT_IMAGE_BASE}/d/dd/RWS_Tarot_20_Judgement.jpg/150px-RWS_Tarot_20_Judgement.jpg`,
    keywords: ['reflection', 'reckoning', 'awakening', 'rebirth'],
    uprightMeaning: 'A calling awakens. Reflect on your journey and answer your higher purpose.',
    reversedMeaning: 'Self-doubt blocks transformation. Release judgment to embrace renewal.',
  },
  {
    number: 21,
    name: 'The World',
    emoji: '🌍',
    image: `${TAROT_IMAGE_BASE}/f/ff/RWS_Tarot_21_World.jpg/150px-RWS_Tarot_21_World.jpg`,
    keywords: ['completion', 'integration', 'accomplishment', 'travel'],
    uprightMeaning: 'A cycle completes in triumph. Celebrate your achievements as new horizons beckon.',
    reversedMeaning: 'Incomplete cycles or delays. What loose ends need tying before moving on?',
  },
]
